import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { featureFlags, hasSocialAuthEnabled } from '../lib/featureFlags';
import { getSafeRedirect } from '../utils/safeRedirect';
import { captureError } from '../lib/capture-error';
import './AuthPage.css';

const AuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const redirectTo = getSafeRedirect(searchParams.get('redirect'));
  const [mode, setMode] = useState<'signin' | 'signup' | 'reset'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const { signIn, signUp, signInWithProvider, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    // Handle password reset
    if (mode === 'reset') {
      if (!email) {
        setError('Please enter your email address');
        return;
      }
      setLoading(true);
      try {
        const { error: resetError } = await resetPassword(email);
        if (resetError) {
          setError(resetError.message);
        } else {
          setMessage('Check your email for a password reset link! (Local dev: check Mailpit at http://127.0.0.1:54324)');
        }
      } catch (err) {
        setError('An unexpected error occurred');
        captureError(err);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (mode === 'signup' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'signup') {
        const { error: signUpError } = await signUp(email, password);
        if (signUpError) {
          setError(signUpError.message);
        } else {
          setMessage('Check your email for a confirmation link!');
        }
      } else {
        const { error: signInError } = await signIn(email, password);
        if (signInError) {
          setError(signInError.message);
        } else {
          navigate(redirectTo);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
      captureError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialAuth = async (provider: 'google' | 'facebook' | 'apple') => {
    setError(null);
    setLoading(true);

    // Store redirect URL for after OAuth callback
    if (redirectTo !== '/dashboard') {
      sessionStorage.setItem('authRedirect', redirectTo);
    }

    try {
      await signInWithProvider(provider);
    } catch (err) {
      setError(`Failed to sign in with ${provider}`);
      captureError(err);
      setLoading(false);
    }
  };

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card shadow">
            <div className="card-body p-5">
              <h2 className="text-center mb-4">
                {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
              </h2>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {message && (
                <div className="alert alert-success" role="alert">
                  {message}
                </div>
              )}

              {/* Social Auth Buttons - controlled by feature flags */}
              {mode !== 'reset' && hasSocialAuthEnabled() && (
                <>
                  <div className="mb-4">
                    {featureFlags.enableGoogleAuth && (
                      <button
                        type="button"
                        className="btn btn-outline-dark w-100 mb-2 d-flex align-items-center justify-content-center"
                        onClick={() => handleSocialAuth('google')}
                        disabled={loading}
                      >
                        <i className="fab fa-google me-2"></i>
                        Continue with Google
                      </button>
                    )}
                    {featureFlags.enableFacebookAuth && (
                      <button
                        type="button"
                        className="btn btn-outline-primary w-100 mb-2 d-flex align-items-center justify-content-center"
                        onClick={() => handleSocialAuth('facebook')}
                        disabled={loading}
                      >
                        <i className="fab fa-facebook me-2"></i>
                        Continue with Facebook
                      </button>
                    )}
                    {featureFlags.enableAppleAuth && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary w-100 mb-2 d-flex align-items-center justify-content-center"
                        onClick={() => handleSocialAuth('apple')}
                        disabled={loading}
                      >
                        <i className="fab fa-apple me-2"></i>
                        Continue with Apple
                      </button>
                    )}
                  </div>

                  <div className="divider my-4">
                    <span className="divider-text">or</span>
                  </div>
                </>
              )}

              {/* Email/Password Form */}
              <form onSubmit={handleEmailAuth}>
                <div className="mb-3">
                  <label htmlFor="email" className="form-label">
                    Email address
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>

                {mode !== 'reset' && (
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      minLength={6}
                    />
                    <small className="form-text text-muted">
                      At least 6 characters
                    </small>
                  </div>
                )}

                {mode === 'signup' && (
                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      minLength={6}
                    />
                  </div>
                )}

                {mode === 'reset' && (
                  <p className="text-muted mb-3">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-100 mb-3"
                  disabled={loading}
                >
                  {loading ? (
                    mode === 'signin' ? 'Signing in...' : mode === 'signup' ? 'Creating account...' : 'Sending reset link...'
                  ) : (
                    <>
                      <i
                        className={`fas ${
                          mode === 'signin'
                            ? 'fa-sign-in-alt'
                            : mode === 'signup'
                              ? 'fa-user-plus'
                              : 'fa-envelope'
                        } me-2`}
                        aria-hidden
                      ></i>
                      {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Sign Up' : 'Send Reset Link'}
                    </>
                  )}
                </button>
              </form>

              {/* Forgot Password link (only show on signin) */}
              {mode === 'signin' && (
                <div className="text-center mb-2">
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => {
                      setMode('reset');
                      setError(null);
                      setMessage(null);
                    }}
                    disabled={loading}
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              {/* Toggle between sign in and sign up */}
              <div className="text-center">
                {mode === 'reset' ? (
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => {
                      setMode('signin');
                      setError(null);
                      setMessage(null);
                    }}
                    disabled={loading}
                  >
                    Back to Sign In
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={() => {
                      setMode(mode === 'signin' ? 'signup' : 'signin');
                      setError(null);
                      setMessage(null);
                    }}
                    disabled={loading}
                  >
                    {mode === 'signin'
                      ? "Don't have an account? Sign up"
                      : 'Already have an account? Sign in'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
